import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Sun } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = '/api';
const C = { text: '#1A1A18', muted: '#9A9590', accent: '#C67A5C', border: '#E8E3DB' };

const ENERGY_OPTIONS = [
  { value: 1, emoji: '\u{1F629}', label: 'Exhausted' },
  { value: 2, emoji: '\u{1F614}', label: 'Low' },
  { value: 3, emoji: '\u{1F610}', label: 'OK' },
  { value: 4, emoji: '\u{1F60A}', label: 'Good' },
  { value: 5, emoji: '\u{1F929}', label: 'Great' },
];

export function MoodLogModal({ open, onClose, isGentle, onSaved }) {
  const { authHeaders } = useAuth();
  const [energy, setEnergy] = useState(null);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!energy) { toast.error('Select an energy level'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/symptoms/quick-mood`, { energy }, authHeaders());
      toast.success('Mood logged');
      onSaved?.();
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}
      onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
      <div onClick={e => e.stopPropagation()} style={{
        position: 'relative', width: '100%', maxWidth: 420, background: '#FDFBF7',
        borderRadius: 24, padding: '24px 24px 32px', boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
      }}>
        <button data-testid="mood-modal-close" onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer' }}>
          <X style={{ width: 20, height: 20, color: C.muted }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Sun style={{ width: 20, height: 20, color: '#C67A5C' }} />
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>
            How are you feeling right now?
          </h2>
        </div>

        {/* Energy level */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, color: C.muted, fontFamily: "'DM Sans', sans-serif", margin: '16px 0 10px', fontWeight: 600 }}>
            Energy level
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            {ENERGY_OPTIONS.map(o => (
              <button
                key={o.value}
                data-testid={`mood-energy-${o.value}`}
                onClick={() => setEnergy(o.value)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '12px 4px', borderRadius: 14, cursor: 'pointer',
                  border: energy === o.value ? `2px solid ${C.accent}` : `2px solid transparent`,
                  background: energy === o.value ? `${C.accent}10` : '#fff',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 28 }}>{o.emoji}</span>
                <span style={{ fontSize: 10, color: energy === o.value ? C.accent : C.muted, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                  {o.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Optional note */}
        <div style={{ marginBottom: 20 }}>
          {!showNote ? (
            <button onClick={() => setShowNote(true)}
              style={{
                background: 'none', border: 'none', fontSize: 12, color: C.muted,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", padding: 0,
              }}>
              + Add a note (optional)
            </button>
          ) : (
            <div>
              <p style={{ fontSize: 12, color: C.muted, fontFamily: "'DM Sans', sans-serif", margin: '0 0 6px', fontWeight: 600 }}>
                {isGentle ? 'Anything on your mind?' : 'Quick note (optional)'}
              </p>
              <input
                data-testid="mood-note-input"
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Slept poorly, stressed about work..."
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 12, fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif", color: C.text, background: '#fff',
                  border: `1px solid ${C.border}`, outline: 'none',
                }}
              />
            </div>
          )}
        </div>

        <button
          data-testid="mood-save"
          onClick={handleSave}
          disabled={saving || !energy}
          style={{
            width: '100%', padding: '14px', borderRadius: 14, border: 'none',
            background: energy ? C.accent : '#E8E3DB', color: energy ? '#fff' : C.muted,
            fontSize: 15, fontWeight: 600,
            cursor: energy ? 'pointer' : 'default', fontFamily: "'DM Sans', sans-serif",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>,
    document.body
  );
}
